import pinMark from "@/assets/logo_pin.png";
import { cn } from "@/lib/utils";

/**
 * Crisp lockup of the BusinessLocating brand: the gold pin as an image, the
 * words as real text.
 *
 * The baked-in wordmark (logo_new.png) can't be read at nav size. Its tagline
 * is 35px tall inside a 413px-tall canvas, so at a 40px render it lands around
 * 3px — and index.css scales the whole app by zoom:0.75 on top of that, which
 * softens it further. Setting the words in live text sidesteps the downscale
 * entirely and lets the tagline take a legible share of the lockup (half the
 * wordmark size rather than the asset's 0.29×), the same proportions the brand
 * uses in print.
 *
 * The pin itself is a two-tone shape cropped straight out of the original
 * artwork, so the mark stays pixel-faithful — it just isn't carrying any text.
 *
 * Colours come from the palette, so the lockup follows whatever theme (or
 * data-theme override) it sits inside.
 */
export default function BrandWordmark({
  className,
  pinSize = 34,
  alt = "BusinessLocating",
}: {
  className?: string;
  /** Height of the pin in px; the type scales with it. */
  pinSize?: number;
  alt?: string;
}) {
  const word = pinSize * 0.5;

  return (
    <span className={cn("flex items-center gap-2.5", className)} aria-label={alt} role="img">
      <img
        src={pinMark}
        alt=""
        aria-hidden="true"
        style={{ height: pinSize }}
        className="w-auto shrink-0"
        draggable={false}
      />
      <span className="flex flex-col justify-center">
        {/* index.css loads Manrope at 400–800 only, so the light half of the
            wordmark is 400 (a 300 would be synthesised). */}
        <span style={{ fontSize: word }} className="font-sans leading-none tracking-tight text-foreground">
          <span className="font-extrabold">Business</span>
          <span className="font-normal opacity-90">Locating</span>
        </span>
        {/* Tracking is tuned so the tagline runs to roughly the width of the
            wordmark above it, as it does in the original artwork. The negative
            right margin absorbs the trailing letter-space, which would
            otherwise push the block wider than the text actually reads. */}
        <span
          style={{ fontSize: word * 0.46, letterSpacing: "0.3em", marginRight: "-0.3em" }}
          className="mt-[0.4em] font-sans font-medium uppercase leading-none text-muted-foreground"
        >
          We find the perfect fit
        </span>
      </span>
    </span>
  );
}
